import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/lib/cn';

// Helix Button — ink (graphite) is primary; blue accent is rationed to
// links / +Add / View. Compact 28-32px control heights, 8px radius, no
// emoji, soft hover. See .claude/skills/helix-design-system/components/
// core/Button.jsx for the visual reference and §5.5 of conventions.
const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-body font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        // Primary action — graphite ink. Per rule 2, this is the high-
        // emphasis button across the whole product (Sign in, Sign & lock,
        // Update password). NOT blue.
        default:
          'bg-[var(--action-ink)] text-[var(--text-on-ink)] hover:bg-[var(--action-ink-hover)] active:bg-[var(--action-ink-active)]',
        // Accent (blue) — rationed: links, +Add, View. Use sparingly.
        accent:
          'bg-[var(--action-accent)] text-[var(--text-on-accent)] hover:bg-[var(--action-accent-hover)]',
        // Destructive — danger-500.
        destructive:
          'bg-[var(--action-danger)] text-[var(--text-on-accent)] hover:bg-[var(--danger-600)]',
        // Secondary — neutral surface with hairline. The "or sign in
        // with a passkey" companion button uses this.
        secondary:
          'bg-[var(--surface)] text-[var(--text-primary)] border border-[var(--border-helix)] hover:bg-[var(--surface-hover)]',
        // Outline alias kept for shadcn parity.
        outline:
          'bg-[var(--surface)] text-[var(--text-primary)] border border-[var(--border-helix)] hover:bg-[var(--surface-hover)]',
        ghost: 'bg-transparent text-[var(--text-primary)] hover:bg-[var(--surface-hover)]',
        link: 'text-[var(--text-link)] underline-offset-4 hover:underline bg-transparent',
      },
      size: {
        // Helix compact control sizes. md (28px) is default.
        sm: 'h-6 px-2 text-callout',
        default: 'h-8 px-3',
        md: 'h-8 px-3',
        lg: 'h-10 px-4',
        icon: 'h-8 w-8 px-0',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
